// src/pages/QbusAutomations/index.jsx
// Main page for managing Qbus automation flows with scope tabs (System / Sites / Units)
import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Typography,
  Paper,
  TextField,
  Autocomplete,
  Alert,
  Button,
  CircularProgress,
  Tabs,
  Tab,
  Chip
} from '@mui/material';
import {
  Add as AddIcon,
  AccountTree as AutomationIcon,
  Public as SystemIcon,
  Business as SiteIcon,
  MeetingRoom as UnitIcon
} from '@mui/icons-material';
import { useSnackbar } from 'notistack';
import { qbusApi } from '../../services/apiClient';
import { locationsApi } from '../../services/apiClient';
import { usePermissions } from '../../contexts/PermissionContext';
import AutomationList from './AutomationList';
import AutomationEditor from './AutomationEditor';

const SCOPE_TABS = [
  { value: 'system', label: 'System', icon: <SystemIcon fontSize="small" /> },
  { value: 'site', label: 'Sites', icon: <SiteIcon fontSize="small" /> },
  { value: 'unit', label: 'Units', icon: <UnitIcon fontSize="small" /> },
];

const QbusAutomations = () => {
  const { enqueueSnackbar } = useSnackbar();
  const { hasPermission } = usePermissions();
  const isAdmin = hasPermission('admin') || hasPermission('qbus.admin');

  // Scope tab
  const [activeScope, setActiveScope] = useState('site');

  // Location selectors
  const [sites, setSites] = useState([]);
  const [selectedSite, setSelectedSite] = useState(null);
  const [units, setUnits] = useState([]);
  const [selectedUnit, setSelectedUnit] = useState(null);
  const [unitsLoading, setUnitsLoading] = useState(false);

  const [automations, setAutomations] = useState([]);
  const [loading, setLoading] = useState(false);
  const [sitesLoading, setSitesLoading] = useState(true);

  // Editor state
  const [editorOpen, setEditorOpen] = useState(false);
  const [editingAutomation, setEditingAutomation] = useState(null);

  // Computed location ID based on active scope
  const selectedLocationId = activeScope === 'unit'
    ? (selectedUnit?.id || '')
    : (selectedSite?.id || '');

  // Fetch sites on mount
  useEffect(() => {
    (async () => {
      try {
        const response = await locationsApi.getSites();
        const data = response.data;
        setSites(Array.isArray(data) ? data : (data?.sites || data?.data || []));
      } catch (error) {
        console.error('Error fetching sites:', error);
      } finally {
        setSitesLoading(false);
      }
    })();
  }, []);

  // Fetch child locations (units) when site changes and scope is 'unit'
  useEffect(() => {
    if (activeScope !== 'unit' || !selectedSite?.id) {
      setUnits([]);
      setSelectedUnit(null);
      return;
    }

    (async () => {
      setUnitsLoading(true);
      try {
        const response = await locationsApi.getByParent(selectedSite.id);
        const data = response.data;
        setUnits(Array.isArray(data) ? data : (data?.locations || data?.data || []));
      } catch (error) {
        console.error('Error fetching units:', error);
      } finally {
        setUnitsLoading(false);
      }
    })();
  }, [activeScope, selectedSite?.id]);

  // Fetch automations when scope or location changes
  const fetchAutomations = useCallback(async () => {
    if (activeScope === 'system') {
      setLoading(true);
      try {
        const response = await qbusApi.getSystemAutomations();
        setAutomations(response.data.automations || []);
      } catch (error) {
        console.error('Error fetching system automations:', error);
        enqueueSnackbar('Failed to load automations', { variant: 'error' });
      } finally {
        setLoading(false);
      }
      return;
    }

    if (!selectedLocationId) return;
    setLoading(true);
    try {
      const response = await qbusApi.getAutomations(selectedLocationId, { scope: activeScope });
      setAutomations(response.data.automations || []);
    } catch (error) {
      console.error('Error fetching automations:', error);
      enqueueSnackbar('Failed to load automations', { variant: 'error' });
    } finally {
      setLoading(false);
    }
  }, [activeScope, selectedLocationId, enqueueSnackbar]);

  useEffect(() => {
    fetchAutomations();
  }, [fetchAutomations]);

  // Reset selections on scope change
  const handleScopeChange = (_e, newScope) => {
    setActiveScope(newScope);
    setAutomations([]);
    if (newScope === 'system') {
      // No location needed
    } else if (newScope === 'unit') {
      setSelectedUnit(null);
    }
  };

  // Handlers
  const handleCreateNew = () => {
    setEditingAutomation(null);
    setEditorOpen(true);
  };

  const handleEdit = async (automationId) => {
    try {
      const response = await qbusApi.getAutomation(automationId);
      setEditingAutomation(response.data.automation);
      setEditorOpen(true);
    } catch (error) {
      enqueueSnackbar('Failed to load automation', { variant: 'error' });
    }
  };

  const handleToggle = async (automationId) => {
    try {
      await qbusApi.toggleAutomation(automationId);
      fetchAutomations();
    } catch (error) {
      enqueueSnackbar('Failed to toggle automation', { variant: 'error' });
    }
  };

  const handleDelete = async (automationId) => {
    try {
      await qbusApi.deleteAutomation(automationId);
      enqueueSnackbar('Automation deleted', { variant: 'success' });
      fetchAutomations();
    } catch (error) {
      enqueueSnackbar('Failed to delete automation', { variant: 'error' });
    }
  };

  const handleTest = async (automationId) => {
    try {
      const response = await qbusApi.testAutomation(automationId);
      const execution = response.data.execution;
      if (execution.status === 'completed') {
        enqueueSnackbar('Test run completed successfully', { variant: 'success' });
      } else {
        enqueueSnackbar(`Test run ${execution.status}: ${execution.error_message || ''}`, { variant: 'warning' });
      }
    } catch (error) {
      enqueueSnackbar('Test run failed', { variant: 'error' });
    }
  };

  const handleEditorSave = async (data) => {
    try {
      if (editingAutomation) {
        await qbusApi.updateAutomation(editingAutomation.id, data);
        enqueueSnackbar('Automation updated', { variant: 'success' });
      } else if (activeScope === 'system') {
        await qbusApi.createSystemAutomation({ ...data, scope: 'system' });
        enqueueSnackbar('System automation created', { variant: 'success' });
      } else {
        await qbusApi.createAutomation(selectedLocationId, { ...data, scope: activeScope });
        enqueueSnackbar('Automation created', { variant: 'success' });
      }
      setEditorOpen(false);
      setEditingAutomation(null);
      fetchAutomations();
    } catch (error) {
      const validationErrors = error.response?.data?.validationErrors;
      if (validationErrors) {
        enqueueSnackbar(`Validation: ${validationErrors.join(', ')}`, { variant: 'error' });
      } else {
        enqueueSnackbar('Failed to save automation', { variant: 'error' });
      }
    }
  };

  const handleEditorClose = () => {
    setEditorOpen(false);
    setEditingAutomation(null);
  };

  // Import: open editor with pre-loaded flow from JSON
  const handleImport = (data) => {
    setEditingAutomation({
      name: data.name || 'Imported Automation',
      description: data.description || '',
      flow_definition: data.flow_definition,
    });
    setEditorOpen(true);
  };

  if (!isAdmin) {
    return (
      <Alert severity="error" sx={{ m: 3 }}>
        You need admin permissions to access automation management.
      </Alert>
    );
  }

  // Full-screen editor mode
  if (editorOpen) {
    return (
      <AutomationEditor
        automation={editingAutomation}
        locationId={selectedLocationId}
        onSave={handleEditorSave}
        onClose={handleEditorClose}
      />
    );
  }

  // Determine if "New Automation" button should be shown
  const canCreate = activeScope === 'system' || selectedLocationId;

  return (
    <Box sx={{ p: 3 }}>
      {/* Header */}
      <Box sx={{ mb: 3, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
          <AutomationIcon sx={{ fontSize: 32 }} />
          <Box>
            <Typography variant="h5">Automations</Typography>
            <Typography variant="body2" color="text.secondary">
              Define automation flows triggered by events, calls, or schedules
            </Typography>
          </Box>
        </Box>
        {canCreate && (
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={handleCreateNew}
          >
            New Automation
          </Button>
        )}
      </Box>

      {/* Scope Tabs */}
      <Paper sx={{ mb: 3 }}>
        <Tabs
          value={activeScope}
          onChange={handleScopeChange}
          sx={{ borderBottom: 1, borderColor: 'divider' }}
        >
          {SCOPE_TABS.map(tab => (
            <Tab
              key={tab.value}
              value={tab.value}
              label={tab.label}
              icon={tab.icon}
              iconPosition="start"
              sx={{ minHeight: 48 }}
            />
          ))}
        </Tabs>

        {/* Location selector (hidden for system scope) */}
        {activeScope !== 'system' && (
          <Box sx={{ p: 2, display: 'flex', gap: 2 }}>
            <Autocomplete
              options={sites}
              getOptionLabel={(option) => option.friendlyName || option.name || `Site ${option.id}`}
              value={selectedSite}
              onChange={(_e, value) => {
                setSelectedSite(value);
                setSelectedUnit(null);
              }}
              loading={sitesLoading}
              isOptionEqualToValue={(option, value) => option.id === value.id}
              sx={{ flex: 1 }}
              renderInput={(params) => (
                <TextField {...params} label="Select Site" size="small" />
              )}
            />

            {/* Unit selector (only for unit scope) */}
            {activeScope === 'unit' && selectedSite && (
              <Autocomplete
                options={units}
                getOptionLabel={(option) => option.friendlyName || option.name || `Unit ${option.id}`}
                value={selectedUnit}
                onChange={(_e, value) => setSelectedUnit(value)}
                loading={unitsLoading}
                isOptionEqualToValue={(option, value) => option.id === value.id}
                sx={{ flex: 1 }}
                renderInput={(params) => (
                  <TextField {...params} label="Select Unit" size="small" />
                )}
              />
            )}
          </Box>
        )}
      </Paper>

      {/* Scope description */}
      {activeScope === 'system' && (
        <Alert severity="info" sx={{ mb: 2 }}>
          System-wide automations apply to all locations. They have the lowest priority — site and unit automations override them.
        </Alert>
      )}
      {activeScope === 'unit' && (
        <Alert severity="info" sx={{ mb: 2 }}>
          Unit automations apply only to a specific unit and have the highest priority — they override site and system automations.
        </Alert>
      )}

      {/* Content */}
      {activeScope !== 'system' && !selectedLocationId ? (
        <Alert severity="info">
          Select a {activeScope === 'unit' ? 'site and unit' : 'site'} to view and manage its automations.
        </Alert>
      ) : loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
          <CircularProgress />
        </Box>
      ) : (
        <AutomationList
          automations={automations}
          onEdit={handleEdit}
          onToggle={handleToggle}
          onDelete={handleDelete}
          onTest={handleTest}
          onImport={handleImport}
          showScope={activeScope === 'system'}
        />
      )}
    </Box>
  );
};

export default QbusAutomations;
